import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as API from '../../store/sagas/api';
import * as userActions from '../../store/actions/user';
import ErrorMessage from '../../components/ErrorMessage';
import { ShieldIcon, UserIcon, UsersIcon, CreditCardIcon, PhoneIcon, SendIcon } from '../../assets/icons';

const REQUEST_COOLDOWN_MS = 5 * 60 * 1000;
const cooldownStorageKey = (id) => `verification-pending-until-${id}`;
const VERIFF_URL = 'https://www.veriff.com/';
const SUPPORT_EMAIL = 'hello@hansepay.com';

function personFullName(p) {
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || '—';
}

function isKycAwaitingResult(row) {
  if (row.isSuccessful) return false;
  const c = row.completedAt;
  if (c == null) return true;
  const t = new Date(c).getTime();
  if (Number.isNaN(t)) return true;
  return t < new Date('2000-01-01').getTime();
}

function kycRowsForPerson(kycStatus, person) {
  if (!Array.isArray(kycStatus) || !person?.id) return [];
  return kycStatus.filter((row) => {
    if (row?.companyPersonId && row.companyPersonId === person.id) return true;
    const cp = row.companyPerson;
    if (cp?.id && cp.id === person.id) return true;
    if (person.email && cp?.email && String(cp.email).toLowerCase() === String(person.email).toLowerCase()) return true;
    return false;
  });
}

function derivePersonKycState(rows) {
  if (!rows.length) return { label: 'Not started', variant: 'muted', canSend: true };
  if (rows.some((r) => r.isSuccessful === true)) return { label: 'Verified', variant: 'success', canSend: false };
  if (rows.some((r) => isKycAwaitingResult(r))) return { label: 'Pending', variant: 'pending', canSend: true };
  return { label: 'Failed', variant: 'failed', canSend: true };
}

function normalizePersonEmail(email) {
  return String(email ?? '')
    .trim()
    .toLowerCase();
}

function splitCompanyPersons(persons) {
  const list = Array.isArray(persons) ? persons : [];
  const owners = list.filter((p) => p.companyPersonType === 'BeneficialOwner');
  const ownerIds = new Set(owners.map((p) => p.id).filter(Boolean));
  const ownerEmails = new Set(owners.map((p) => normalizePersonEmail(p.email)).filter(Boolean));

  const legalReps = list.filter((p) => {
    if (p.companyPersonType !== 'ControllingPerson' && p.companyPersonType !== 'BeneficialControlling') return false;
    if (!p.id) return false;
    if (ownerIds.has(p.id)) return false;
    const email = normalizePersonEmail(p.email);
    if (email && ownerEmails.has(email)) return false;
    return true;
  });
  return { legalReps, owners };
}

const whatToExpectRowClass = 'flex items-start gap-3 rounded-lg border border-border bg-background p-4';

function WhatToExpect() {
  return (
    <div className='space-y-3'>
      <p className='text-sm font-semibold text-foreground uppercase tracking-wide'>What to expect</p>
      <div className='space-y-3'>
        <div className={whatToExpectRowClass}>
          <div className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10'>
            <UserIcon className='h-4 w-4 text-primary' aria-hidden />
          </div>
          <div className='min-w-0'>
            <p className='font-medium text-foreground'>No person to speak to</p>
            <p className='text-sm text-muted-foreground'>The process is entirely automated — no calls or video interviews required.</p>
          </div>
        </div>
        <div className={whatToExpectRowClass}>
          <div className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10'>
            <CreditCardIcon className='h-4 w-4 text-primary' aria-hidden />
          </div>
          <div className='min-w-0'>
            <p className='font-medium text-foreground'>Have your ID ready</p>
            <p className='text-sm text-muted-foreground'>
              Each person needs a valid government-issued ID — passport, national ID, or driver&apos;s licence.
            </p>
          </div>
        </div>
        <div className={whatToExpectRowClass}>
          <div className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10'>
            <PhoneIcon className='h-4 w-4 text-primary' aria-hidden />
          </div>
          <div className='min-w-0'>
            <p className='font-medium text-foreground'>Keep a phone nearby</p>
            <p className='text-sm text-muted-foreground'>A smartphone is required to complete the verification steps.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_PILL_VARIANT_CLASS = {
  success: 'bg-success/15 text-success',
  failed: 'bg-destructive/10 text-destructive',
  pending: 'bg-muted text-muted-foreground',
  muted: 'bg-muted text-muted-foreground',
};

function StatusPill({ variant, label }) {
  const cls = STATUS_PILL_VARIANT_CLASS[variant] ?? STATUS_PILL_VARIANT_CLASS.muted;
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

StatusPill.propTypes = {
  variant: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

function PersonRow({ person, detailLine, kycState, onSendEmail, sending, disabledUntil, minutesLeft, sentHint }) {
  const cooldownActive = disabledUntil != null && Date.now() < disabledUntil;
  const sendDisabled = sending || !kycState.canSend || cooldownActive;

  let sendLabel = 'Send Email';
  if (sending) sendLabel = 'Sending...';
  else if (cooldownActive && minutesLeft > 0) sendLabel = `Try again in ${minutesLeft} min`;

  return (
    <div className='flex flex-col gap-3 rounded-lg border border-border bg-background p-4 sm:flex-row sm:items-center sm:justify-between'>
      <div className='flex min-w-0 flex-1 items-start gap-3'>
        <div className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10'>
          <ShieldIcon className='h-4 w-4 text-primary' aria-hidden />
        </div>
        <div className='min-w-0'>
          <p className='font-medium text-foreground'>{personFullName(person)}</p>
          {detailLine ? <p className='text-sm text-muted-foreground'>{detailLine}</p> : null}
          {sentHint ? <p className='mt-1 text-xs text-success'>Verification link sent</p> : null}
        </div>
      </div>
      <div className='flex shrink-0 flex-wrap items-center gap-2 sm:justify-end'>
        <StatusPill variant={kycState.variant} label={kycState.label} />
        <button
          type='button'
          onClick={() => onSendEmail(person.id)}
          disabled={sendDisabled}
          className='inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
        >
          <SendIcon className='h-4 w-4' aria-hidden />
          {sendLabel}
        </button>
      </div>
    </div>
  );
}

const personRowPersonShape = PropTypes.shape({
  id: PropTypes.string,
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  companyPersonType: PropTypes.string,
  companyOwnershipPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyRole: PropTypes.string,
  isDirector: PropTypes.bool,
});

const kycStateShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  variant: PropTypes.string.isRequired,
  canSend: PropTypes.bool.isRequired,
});

const kycStatusRowShape = PropTypes.shape({
  isSuccessful: PropTypes.bool,
  completedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  companyPersonId: PropTypes.string,
  companyPerson: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
  }),
});

PersonRow.propTypes = {
  person: personRowPersonShape.isRequired,
  detailLine: PropTypes.string,
  kycState: kycStateShape.isRequired,
  onSendEmail: PropTypes.func.isRequired,
  sending: PropTypes.bool.isRequired,
  disabledUntil: PropTypes.number,
  minutesLeft: PropTypes.number.isRequired,
  sentHint: PropTypes.bool,
};

function PersonSection({ title, description, icon: Icon, persons, kycStatus, onSendEmail, sendingId, cooldowns, sentHintId }) {
  if (!persons.length) return null;
  return (
    <div className='rounded-xl border border-border bg-card p-5 shadow-sm'>
      <div className='mb-4 flex items-start gap-3'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10'>
          <Icon className='h-5 w-5 text-primary' aria-hidden />
        </div>
        <div className='min-w-0 grow'>
          <h2 className='break-words text-lg font-semibold text-foreground'>{title}</h2>
          <p className='text-sm text-muted-foreground'>{description}</p>
        </div>
      </div>
      <div className='space-y-3'>
        {persons.map((person) => {
          const rows = kycRowsForPerson(kycStatus, person);
          const kycState = derivePersonKycState(rows);
          const key = person.id || person.email;
          const until = cooldowns[person.id];
          const minutesLeft = until != null && until > Date.now() ? Math.ceil((until - Date.now()) / 60000) : 0;
          return (
            <PersonRow
              key={key}
              person={person}
              detailLine={
                person.companyPersonType === 'BeneficialOwner'
                  ? `${person.companyOwnershipPercentage ?? '—'}% ownership`
                  : person.companyRole || (person.isDirector ? 'Director' : null) || 'Representative'
              }
              kycState={kycState}
              onSendEmail={onSendEmail}
              sending={sendingId === person.id}
              disabledUntil={until}
              minutesLeft={minutesLeft}
              sentHint={sentHintId === person.id}
            />
          );
        })}
      </div>
    </div>
  );
}

PersonSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  persons: PropTypes.arrayOf(personRowPersonShape).isRequired,
  kycStatus: PropTypes.arrayOf(kycStatusRowShape),
  onSendEmail: PropTypes.func.isRequired,
  sendingId: PropTypes.string,
  cooldowns: PropTypes.objectOf(PropTypes.number).isRequired,
  sentHintId: PropTypes.string,
};

function CompanyKycVerification() {
  const dispatch = useDispatch();
  const { kycStatus, getKycStatusLoading, getKycStatusError } = useSelector((state) => state.user);
  const [persons, setPersons] = useState([]);
  const [loadPersonsError, setLoadPersonsError] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [sentHintId, setSentHintId] = useState(null);
  const [cooldowns, setCooldowns] = useState({});

  const refreshCooldownsFromStorage = useCallback((list) => {
    const next = {};
    list.forEach((p) => {
      if (!p.id) return;
      const raw = localStorage.getItem(cooldownStorageKey(p.id));
      if (raw) {
        const until = parseInt(raw, 10);
        if (until > Date.now()) next[p.id] = until;
        else localStorage.removeItem(cooldownStorageKey(p.id));
      }
    });
    setCooldowns(next);
  }, []);

  useEffect(() => {
    dispatch(userActions.getKycStatus());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await API.getCompanyPersons();
        if (!cancelled) {
          setPersons(Array.isArray(data) ? data : []);
          refreshCooldownsFromStorage(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) setLoadPersonsError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshCooldownsFromStorage]);

  const { legalReps, owners } = useMemo(() => splitCompanyPersons(persons), [persons]);

  const allRequiredVerified = useMemo(() => {
    const required = [...legalReps, ...owners];
    if (!required.length) return false;
    return required.every((p) => derivePersonKycState(kycRowsForPerson(kycStatus, p)).label === 'Verified');
  }, [legalReps, owners, kycStatus]);

  const handleSendEmail = async (id) => {
    if (!id) return;
    setRequestError(null);
    setSentHintId(null);
    setSendingId(id);
    try {
      await API.requestCompanyPersonKyc(id);
      const until = Date.now() + REQUEST_COOLDOWN_MS;
      localStorage.setItem(cooldownStorageKey(id), String(until));
      setCooldowns((prev) => ({ ...prev, [id]: until }));
      setSentHintId(id);
      dispatch(userActions.getKycStatus());
    } catch (e) {
      setRequestError(e);
    } finally {
      setSendingId(null);
    }
  };

  const displayError =
    requestError?.message ??
    requestError?.detail ??
    loadPersonsError?.message ??
    loadPersonsError?.detail ??
    getKycStatusError?.message ??
    getKycStatusError?.detail;

  return (
    <div className='w-full max-w-3xl mx-auto px-4 py-6 space-y-8'>
      <div className='text-center space-y-3'>
        <div className='mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-warning/10'>
          <ShieldIcon className='h-8 w-8 text-warning' aria-hidden />
        </div>
        <h1 className='text-2xl font-bold text-foreground'>Verify company management &amp; owners</h1>
        <p className='text-muted-foreground leading-relaxed max-w-xl mx-auto'>
          Each person listed below must complete identity verification. We&apos;ll email them a secure link to our partner{' '}
          <a href={VERIFF_URL} target='_blank' rel='noopener noreferrer' className='text-primary hover:underline font-medium'>
            Veriff
          </a>
          .
        </p>
      </div>

      <WhatToExpect />

      {getKycStatusLoading && persons.length === 0 && !loadPersonsError ? (
        <p className='text-sm text-muted-foreground text-center'>Loading...</p>
      ) : null}

      <PersonSection
        title='Company Management'
        description='Each member of company management must complete identity verification.'
        icon={UserIcon}
        persons={legalReps}
        kycStatus={kycStatus}
        onSendEmail={handleSendEmail}
        sendingId={sendingId}
        cooldowns={cooldowns}
        sentHintId={sentHintId}
      />

      <PersonSection
        title='Company Owners'
        description='Each company owner must complete identity verification.'
        icon={UsersIcon}
        persons={owners}
        kycStatus={kycStatus}
        onSendEmail={handleSendEmail}
        sendingId={sendingId}
        cooldowns={cooldowns}
        sentHintId={sentHintId}
      />

      {legalReps.length === 0 && owners.length === 0 && !getKycStatusLoading && !loadPersonsError ? (
        <p className='text-center text-sm text-muted-foreground'>
          No company persons found. Complete the previous step to add management and owners.
        </p>
      ) : null}

      {allRequiredVerified && legalReps.length + owners.length > 0 ? (
        <p className='text-center text-sm font-medium text-success'>Everyone required has completed verification. You can continue onboarding.</p>
      ) : null}

      <div className='text-center pt-2'>
        <p className='text-xs text-muted-foreground leading-relaxed'>
          Need help? Contact us at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className='text-primary hover:underline'>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>

      {displayError && <ErrorMessage error={displayError} />}
    </div>
  );
}

export default CompanyKycVerification;
